// A simple, robust way to handle environment variables in Vite.

// Runtime-safe env reader. In the browser/Vite build `import.meta.env` exists.
// In Node runtimes (Vercel Workflow DevKit steps, edge fns) `import.meta` has
// no `env` — accessing `import.meta.env[key]` there throws
// "Cannot read properties of undefined (reading 'VITE_...')" at MODULE LOAD,
// which crashed the entire video/workflow step bundle before any code ran.
// Fall back to `process.env` so this module loads safely in both runtimes.
const readEnv = (key: string): string | undefined => {
  try {
    if (typeof import.meta !== 'undefined') {
      const metaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
      if (metaEnv && metaEnv[key] !== undefined) return metaEnv[key];
    }
  } catch {
    // import.meta unsupported in this runtime — fall through
  }
  if (typeof process !== 'undefined' && process.env) return process.env[key];
  return undefined;
};

const getMode = (): string => readEnv('MODE') ?? readEnv('NODE_ENV') ?? 'production';

const getRequired = (key: string): string => {
  const value = readEnv(key);
  if (value === undefined || value === null) {
    // In a test environment, it's okay for some vars to be missing.
    if (getMode() !== 'test') {
      throw new Error(`Missing required environment variable: ${key}`);
    }
    return `dummy_${key}`; // Provide a default for tests
  }
  return value;
};

const getOptional = (key: string): string | undefined => {
  return readEnv(key);
};

const config = {
  supabase: {
    url: getRequired('VITE_SUPABASE_URL'),
    anonKey: getRequired('VITE_SUPABASE_ANON_KEY'),
    // NOTE: Service role key is a backend secret. It will be undefined on the client.
    serviceRoleKey: getOptional('SUPABASE_SERVICE_ROLE_KEY'),
  },
  app: {
    environment:
      (getOptional('VITE_APP_ENV') as 'development' | 'staging' | 'production') || 'development',
    debug: getOptional('VITE_DEBUG') === 'true',
    version: getOptional('VITE_APP_VERSION') || '1.2.1',
    baseUrl: getOptional('VITE_APP_BASE_URL') || 'https://app.mora-marketer.com',
  },
  // NOTE: Social secrets and AI keys are backend-only and not included here.
  // The frontend only needs the public client IDs.
  social: {
    facebookAppId: getOptional('VITE_FACEBOOK_APP_ID'),
    instagramAppId: getOptional('VITE_INSTAGRAM_APP_ID'),
    linkedinClientId: getOptional('VITE_LINKEDIN_CLIENT_ID'),
    xClientId: getOptional('VITE_X_CLIENT_ID'),
    tiktokClientId: getOptional('VITE_TIKTOK_CLIENT_ID'),
    shopifyDirectClientId: getOptional('VITE_SHOPIFY_DIRECT_CLIENT_ID'),
  },
  services: {
    // Sentry DSN for frontend error tracking should be prefixed with VITE_
    sentryDsn: getOptional('VITE_SENTRY_DSN'),
  },
};

// Runtime validation for critical configuration
// Note: Previously flagged iqp3v7pbua-uc URLs, but this is actually our current production deployment
// The warning was incorrectly suggesting an older URL as the "correct" one

export const env = {
  ...config,
  isDevelopment: () => config.app.environment === 'development',
  isProduction: () => config.app.environment === 'production',
  isStaging: () => config.app.environment === 'staging',
  isDebugEnabled: () => config.app.debug,

  // Helper method to check if a service is configured
  isServiceConfigured: (
    service: 'facebook' | 'linkedin' | 'twitter' | 'tiktok' | 'shopify' | 'sentry',
  ) => {
    switch (service) {
      case 'facebook':
        return !!config.social.facebookAppId;
      case 'linkedin':
        return !!config.social.linkedinClientId;
      case 'twitter':
        return !!config.social.xClientId;
      case 'tiktok':
        return !!config.social.tiktokClientId;
      case 'shopify':
        return !!config.social.shopifyDirectClientId;
      case 'sentry':
        return !!config.services.sentryDsn;
      default:
        return false;
    }
  },
};

// Legacy exports for backward compatibility
export const isProduction = () => env.isProduction();
export const isDevelopment = () => env.isDevelopment();
export const isStaging = () => env.isStaging();
export const isDebugEnabled = () => env.isDebugEnabled();
